import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import RecordDialog from "@/components/RecordDialog";
import { RESOURCES } from "@/lib/config";
import { toast } from "sonner";
import { ArrowRightCircle, CheckCircle2, Send, Check } from "lucide-react";

export function ConvertLead({ row, reload }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (row.converted_party_id) {
    return <Button size="sm" variant="ghost" className="h-7 text-xs text-seagreen" data-testid={`open-converted-${row.id}`} onClick={() => nav(`/clients/${row.converted_party_id}`)}>
      <Check className="h-3.5 w-3.5 mr-1" /> {row.lifecycle || "Converted"} — Open
    </Button>;
  }
  // Conversion advances LIFECYCLE (Prospect/Client). Entity type is preserved server-side from the lead.
  const convert = async (target) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${row.id}/convert`, { target });
      toast.success(`Lead converted to ${target}.`);
      if (data?.id) nav(`/clients/${data.id}`); else reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-atlantic" data-testid={`convert-prospect-${row.id}`} onClick={() => convert("Prospect")}>
        <ArrowRightCircle className="h-3.5 w-3.5 mr-1" /> To Prospect
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} className="h-7 text-xs text-navy" data-testid={`convert-client-${row.id}`} onClick={() => convert("Client")}>
        <ArrowRightCircle className="h-3.5 w-3.5 mr-1" /> To Client
      </Button>
    </div>
  );
}

export function ConvertOpp({ row, reload }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (row.converted_policy_id) return <span className="text-xs text-seagreen">Bound → Policy</span>;
  if (row.stage !== "Bound") return <span className="text-xs text-mutedsea">—</span>;
  return (
    <>
      <Button size="sm" className="h-7 text-xs bg-navy hover:bg-atlantic text-white" data-testid={`convert-opp-${row.id}`} onClick={() => setOpen(true)}>
        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> To Policy
      </Button>
      {open && (
        <RecordDialog open onOpenChange={() => setOpen(false)}
          fields={[
            { key: "policy_number", label: "Policy Number", type: "text", required: true },
            { key: "carrier_name", label: "Carrier", type: "carrier", lookup: "carriers" },
            { key: "line", label: "Line", type: "text" },
            { key: "named_insured", label: "Named Insured", type: "text" },
            { key: "premium", label: "Premium", type: "number" },
            { key: "effective_date", label: "Effective Date", type: "date" },
            { key: "expiration_date", label: "Expiration Date", type: "date" },
          ]}
          title={`Convert Opportunity to Policy`} initial={{ premium: row.expected_premium, line: row.lines_requested }}
          endpoint={`/opportunities/${row.id}/convert`} method="post"
          onSaved={(pol) => { setOpen(false); toast.success("Policy created from opportunity."); if (pol?.id) nav(`/policies/${pol.id}`); else reload(); }} />
      )}
    </>
  );
}

export function CertActions({ row, reload }) {
  const act = async (verb) => {
    try { await api.post(`/certificates/${row.id}/${verb}`); toast.success(`Certificate ${verb}d.`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-1 justify-end">
      {(row.additional_insured_request || row.non_standard_wording) && row.status !== "Approved" && row.status !== "Issued" && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-seaamber" data-testid={`approve-cert-${row.id}`} onClick={() => act("approve")}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
      )}
      {row.status !== "Issued" && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-atlantic" data-testid={`issue-cert-${row.id}`} onClick={() => act("issue")}><Send className="h-3.5 w-3.5 mr-1" /> Issue</Button>
      )}
      {row.status === "Issued" && <span className="text-xs text-seagreen">Issued</span>}
    </div>
  );
}

export function TaskComplete({ row, reload }) {
  if (row.status === "Completed") return <span className="text-xs text-seagreen">Done</span>;
  const complete = async () => {
    try { await api.put(`/tasks/${row.id}`, { status: "Completed", completed_date: new Date().toISOString().slice(0, 10) }); toast.success("Task completed."); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return <Button size="sm" variant="ghost" className="h-7 text-xs text-seagreen" data-testid={`complete-task-${row.id}`} onClick={complete}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete</Button>;
}
